// LibraryReader — the MasterClass-style reading experience.
// Full-bleed dark theater, chapter sidebar, progress tracking, audio-narration
// toggle, resume-where-you-left-off, and "what to read next" at the end.
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { getWithRetry } from "../lib/apiResilient";
import { toast } from "sonner";
import { usePortalAuth } from "../portal/PortalAuth";
import {
  ChevronLeft, ChevronRight, BookOpen, ListOrdered, CheckCircle2,
  Sun, Moon, Maximize2, Minimize2, Sparkles, ArrowUpRight, Volume2, VolumeX, Loader2, X,
  Briefcase,
} from "lucide-react";
import PdfReader from "../components/PdfReader";
import FieldNotes from "../components/FieldNotes";
import FinalReflection from "../components/FinalReflection";
import EditorsToolkit from "../components/EditorsToolkit";
import MarginNote from "../components/MarginNote";
import WelcomeOverlay from "../components/WelcomeOverlay";

const API = process.env.REACT_APP_BACKEND_URL;
const CHAPTER_SIZE = 4; // PDF pages per "chapter" — granular enough to feel like lessons

export default function LibraryReader() {
  const { slug } = useParams();
  const { user } = usePortalAuth();
  const navigate = useNavigate();

  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [viewerUrl, setViewerUrl] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [readingMode, setReadingMode] = useState("dark"); // "dark" | "light"
  const [fullscreen, setFullscreen] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [showFinished, setShowFinished] = useState(false);
  const [recs, setRecs] = useState([]);
  const [narrating, setNarrating] = useState(false);
  const [toolkitOpen, setToolkitOpen] = useState(false);
  const [discoveredPages, setDiscoveredPages] = useState(0);  // total page count discovered by PDF.js

  // ─── Load metadata; for PDFs also fetch the blob in parallel ───────
  useEffect(() => {
    let cancelled = false; let revokedUrl = null;
    (async () => {
      try {
        // Fetch meta first to decide if we need the PDF blob (skip for audio).
        const m = await getWithRetry(`${API}/api/library/meta/${slug}`, { withCredentials: true });
        if (cancelled) return;
        setMeta(m.data);
        setCompleted(!!m.data.completed);
        setCurrentPage(Math.max(1, m.data.last_page || 1));
        // PDF? Pull the binary so the iframe can render it from a blob URL
        if (m.data.kind !== "audio") {
          const pdfRes = await getWithRetry(
            `${API}/api/library/view/${slug}`,
            { withCredentials: true, responseType: "blob" }
          );
          if (cancelled) return;
          const blob = new Blob([pdfRes.data], { type: "application/pdf" });
          const url = URL.createObjectURL(blob);
          revokedUrl = url;
          setViewerUrl(url);
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Couldn't open this edition.");
      }
    })();
    return () => {
      cancelled = true;
      if (revokedUrl) URL.revokeObjectURL(revokedUrl);
    };
  }, [slug]);

  // Recommendations — fetched once, used at the end-of-edition celebration
  useEffect(() => {
    axios.get(`${API}/api/library/recommendations?limit=4`, { withCredentials: true })
      .then((r) => setRecs(r.data.items || []))
      .catch(() => {});
  }, []);

  // ─── Persist progress every time we change page ──────────────────────
  useEffect(() => {
    if (!meta) return;
    const t = setTimeout(() => {
      axios.post(
        `${API}/api/library/track/${slug}`,
        { page: currentPage, total_pages: meta.page_count || undefined },
        { withCredentials: true }
      ).then((r) => {
        if (r.data?.completed && !completed) {
          setCompleted(true);
          setShowFinished(true);
        }
      }).catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [currentPage, meta, slug, completed]);

  // ─── Navigate the embedded PDF viewer to a given page ────────────────
  // No more iframe.src reload — we use react-pdf with stateful pageNumber.
  const totalKnownPages = meta?.page_count || discoveredPages;
  const jumpToPage = (p) => {
    const total = totalKnownPages || 999;
    const next = Math.max(1, Math.min(total, p));
    setCurrentPage(next);
  };

  // ─── Chapters derived from page count ────────────────────────────────
  const chapters = useMemo(() => {
    const total = totalKnownPages;
    if (!total) return [];
    const list = [];
    let n = 1;
    for (let p = 1; p <= total; p += CHAPTER_SIZE) {
      const end = Math.min(p + CHAPTER_SIZE - 1, total);
      list.push({
        index: n,
        title: n === 1 ? "Opening" : (p + CHAPTER_SIZE - 1 >= total ? "Closing chapter" : `Chapter ${n}`),
        startPage: p,
        endPage: end,
      });
      n += 1;
    }
    return list;
  }, [totalKnownPages]);

  const activeChapterIdx = useMemo(() => {
    return chapters.findIndex((c) => currentPage >= c.startPage && currentPage <= c.endPage);
  }, [chapters, currentPage]);

  const progressPct = totalKnownPages ? Math.round((currentPage / totalKnownPages) * 100) : 0;
  const isLightMode = readingMode === "light";

  // ─── Toggle "completed" manually ────────────────────────────────────
  const handleMarkComplete = async (val) => {
    try {
      await axios.post(
        `${API}/api/library/complete/${slug}`,
        { completed: val },
        { withCredentials: true }
      );
      setCompleted(val);
      if (val) {
        toast.success("Marked as finished. Nicely done.");
        setShowFinished(true);
      } else {
        toast.success("Back to in-progress.");
        setShowFinished(false);
      }
    } catch {
      toast.error("Couldn't update progress. Try again.");
    }
  };

  // ─── Audio narration via the browser's SpeechSynthesis API ───────────
  // We synthesize the title + chapter prompt — a "headline read" experience
  // that gives a premium audio touch without needing real recordings.
  useEffect(() => {
    return () => { if (typeof window !== "undefined") window.speechSynthesis?.cancel?.(); };
  }, []);
  const toggleNarration = () => {
    if (typeof window === "undefined" || !window.speechSynthesis) {
      toast.error("Your browser doesn't support narration.");
      return;
    }
    if (narrating) {
      window.speechSynthesis.cancel();
      setNarrating(false);
      return;
    }
    const phrase = `${meta?.title}. Edition ${meta?.edition_no}. ${chapters[activeChapterIdx]?.title || "Opening"}.`;
    const u = new SpeechSynthesisUtterance(phrase);
    u.rate = 0.95; u.pitch = 1.0;
    u.onend = () => setNarrating(false);
    window.speechSynthesis.speak(u);
    setNarrating(true);
  };

  // ─── Error & loading states ─────────────────────────────────────────
  if (error) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="overline text-ink/55 mb-4">Edition · Issue</div>
          <p className="font-serif italic text-xl text-ink/80 mb-6">{error}</p>
          <button onClick={() => navigate("/library")} className="overline text-ink border-b border-ink/40 pb-1">Back to your shelf</button>
        </div>
      </div>
    );
  }

  if (!meta || (meta.kind !== "audio" && !viewerUrl)) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center">
        <div className="text-cream/60 overline animate-pulse flex items-center gap-3">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading edition…
        </div>
      </div>
    );
  }

  const isAudio = meta.kind === "audio";

  // ─── Theme palette ──────────────────────────────────────────────────
  const palette = isLightMode
    ? { bg: "bg-cream", text: "text-ink", panel: "bg-white", panelBorder: "border-ink/15", subtle: "text-ink/60", subtler: "text-ink/45" }
    : { bg: "bg-ink", text: "text-cream", panel: "bg-ink/95", panelBorder: "border-cream/15", subtle: "text-cream/65", subtler: "text-cream/45" };

  return (
    <div className={`min-h-screen ${palette.bg} ${palette.text} flex flex-col`} data-testid="library-reader-page">
      {/* Welcome overlay — only fires on `?welcome=1&to=…` (post-checkout) */}
      <WelcomeOverlay editionTitle={meta?.title} />

      {/* TOP BAR — editorial chrome */}
      <header className={`flex-shrink-0 border-b ${palette.panelBorder} ${palette.panel}`}>
        <div className="flex items-center justify-between gap-4 px-4 md:px-8 py-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate("/library")}
              className={`flex items-center gap-2 ${palette.subtle} hover:opacity-100 opacity-80 transition-opacity`}
              data-testid="reader-back"
              aria-label="Back to shelf"
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="overline hidden sm:inline">Shelf</span>
            </button>
            <div className={`hidden sm:block h-4 w-px ${isLightMode ? "bg-ink/20" : "bg-cream/20"}`} />
            <div className="min-w-0">
              <div className={`overline ${palette.subtle} text-[9px]`}>№ {meta.edition_no} · {meta.tier_label}</div>
              <div className="font-serif text-[15px] leading-tight truncate max-w-[60vw] md:max-w-md">{meta.title}</div>
            </div>
          </div>

          <div className="flex items-center gap-1 md:gap-2">
            <IconBtn onClick={() => setToolkitOpen(true)} title="Editor's Toolkit" testId="reader-toolkit" light={isLightMode}>
              <Briefcase className="h-4 w-4" />
            </IconBtn>
            <IconBtn onClick={() => setSidebarOpen(s => !s)} title="Chapters" testId="reader-toggle-sidebar" light={isLightMode}>
              <ListOrdered className="h-4 w-4" />
            </IconBtn>
            <IconBtn onClick={toggleNarration} title={narrating ? "Stop narration" : "Read aloud"} testId="reader-narrate" light={isLightMode}>
              {narrating ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </IconBtn>
            <IconBtn onClick={() => setReadingMode(m => m === "dark" ? "light" : "dark")} title="Reading mode" testId="reader-theme" light={isLightMode}>
              {isLightMode ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
            </IconBtn>
            <IconBtn onClick={() => setFullscreen(f => !f)} title="Fullscreen" testId="reader-fullscreen" light={isLightMode}>
              {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </IconBtn>
            <button
              onClick={() => handleMarkComplete(!completed)}
              data-testid="reader-mark-complete"
              className={`hidden md:inline-flex items-center gap-2 overline tracking-[0.22em] text-[11px] px-3 py-2 border ${completed ? "border-emerald-500/60 text-emerald-500" : (isLightMode ? "border-ink/30 hover:bg-ink hover:text-cream" : "border-cream/30 hover:bg-cream hover:text-ink")} transition-colors`}
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              {completed ? "Finished" : "Mark finished"}
            </button>
          </div>
        </div>
        {/* progress bar */}
        <div className={`h-[2px] w-full ${isLightMode ? "bg-ink/8" : "bg-cream/10"}`}>
          <div
            className={`h-full ${completed ? "bg-emerald-500" : (isLightMode ? "bg-ink" : "bg-cream")} transition-[width] duration-500 ease-out`}
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </header>

      {/* BODY — sidebar + reader */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Sidebar — drawer on mobile, persistent on desktop */}
        {sidebarOpen && (
          <>
            {/* Mobile backdrop */}
            <div
              className="md:hidden fixed inset-0 z-40 bg-ink/60"
              onClick={() => setSidebarOpen(false)}
            />
            <aside
              className={`fixed md:relative inset-y-0 left-0 z-50 w-72 md:w-64 lg:w-72 flex-shrink-0 flex flex-col border-r ${palette.panelBorder} ${palette.panel}`}
              data-testid="reader-sidebar"
            >
              <div className={`px-5 py-4 border-b ${palette.panelBorder} flex items-start justify-between gap-2`}>
                <div>
                  <div className={`overline ${palette.subtle} text-[9px]`}>Edition contents</div>
                  <div className="font-display text-2xl mt-1">
                    {isAudio ? `${(meta.episodes||[]).length} episode${(meta.episodes||[]).length === 1 ? "" : "s"}` : `${totalKnownPages || "—"} pages`}
                  </div>
                  {!isAudio && totalKnownPages && (
                    <div className={`text-[11px] ${palette.subtler} font-serif italic mt-1`}>
                      ~{Math.max(1, Math.round((totalKnownPages * 2.5) / 5) * 5)} min read
                    </div>
                  )}
                  {isAudio && (
                    <div className={`text-[11px] ${palette.subtler} font-serif italic mt-1`}>
                      Audio series · listen in any order
                    </div>
                  )}
                </div>
                <button
                  className={`md:hidden p-2 ${palette.subtle}`}
                  onClick={() => setSidebarOpen(false)}
                  aria-label="Close chapters"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            <div className="flex-1 overflow-y-auto px-2 py-3">
              {chapters.map((c, idx) => {
                const isActive = idx === activeChapterIdx;
                const isPassed = currentPage > c.endPage;
                return (
                  <button
                    key={c.index}
                    onClick={() => jumpToPage(c.startPage)}
                    data-testid={`reader-chapter-${c.index}`}
                    className={`w-full text-left px-3 py-3 flex items-start gap-3 transition-colors ${isActive ? (isLightMode ? "bg-ink text-cream" : "bg-cream text-ink") : (isLightMode ? "hover:bg-ink/5" : "hover:bg-cream/8")}`}
                  >
                    <div className={`mt-0.5 font-display text-sm tracking-[0.15em] w-6 text-right ${isActive ? "opacity-90" : "opacity-50"}`}>
                      {String(c.index).padStart(2, "0")}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] leading-tight font-serif">{c.title}</div>
                      <div className={`text-[10px] mt-0.5 overline opacity-60`}>
                        p.{c.startPage}{c.endPage !== c.startPage ? `–${c.endPage}` : ""}
                      </div>
                    </div>
                    {isPassed && <CheckCircle2 className="h-3.5 w-3.5 mt-1 opacity-50" />}
                  </button>
                );
              })}
            </div>
            {/* sidebar footer — completion + download */}
            <div className={`border-t ${palette.panelBorder} p-4 space-y-2`}>
              <button
                onClick={() => handleMarkComplete(!completed)}
                data-testid="reader-sidebar-complete"
                className={`w-full overline text-[10px] tracking-[0.22em] py-3 border ${completed ? "border-emerald-500/60 text-emerald-500" : (isLightMode ? "border-ink/30 hover:bg-ink hover:text-cream" : "border-cream/30 hover:bg-cream hover:text-ink")} transition-colors`}
              >
                {completed ? "✓ Finished — undo" : "Mark this edition finished"}
              </button>
              <a
                href={`${API}/api/library/view/${slug}`}
                download={`${slug}.pdf`}
                className={`block w-full text-center overline text-[10px] tracking-[0.22em] py-3 border ${isLightMode ? "border-ink/20 hover:border-ink/40" : "border-cream/20 hover:border-cream/40"} transition-colors`}
                data-testid="reader-download-pdf"
              >
                Download PDF
              </a>
            </div>
          </aside>
          </>
        )}

        {/* PDF reader OR audio player area */}
        <main className="flex-1 relative overflow-hidden">
          {isAudio ? (
            <PodcastPlayer
              slug={slug}
              episodes={meta.episodes || []}
              title={meta.title}
              palette={palette}
              isLightMode={isLightMode}
            />
          ) : (
            <>
              <PdfReader
                fileUrl={viewerUrl}
                currentPage={currentPage}
                onPageChange={jumpToPage}
                onTotalPages={(n) => { setDiscoveredPages(n); }}
                totalPages={totalKnownPages}
                isLightMode={isLightMode}
              />

              {/* Floating bottom navigator — pinned to bottom-left so it
                  never collides with the bottom-right Notes button. */}
              <div className={`absolute bottom-4 left-3 sm:left-6 flex items-center gap-1 px-2 py-1.5 border ${palette.panelBorder} ${palette.panel} shadow-lg z-20 rounded-full`}>
                <button
                  onClick={() => jumpToPage(currentPage - 1)}
                  disabled={currentPage <= 1}
                  data-testid="reader-prev"
                  className={`p-3 ${palette.subtle} hover:opacity-100 opacity-90 disabled:opacity-30 transition-opacity`}
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <div className="overline text-[11px] tabular-nums min-w-[80px] text-center px-2" data-testid="reader-page-label">
                  p.{currentPage}{totalKnownPages ? ` / ${totalKnownPages}` : ""}
                </div>
                <button
                  onClick={() => jumpToPage(currentPage + 1)}
                  disabled={totalKnownPages && currentPage >= totalKnownPages}
                  data-testid="reader-next"
                  className={`p-3 ${palette.subtle} hover:opacity-100 opacity-90 disabled:opacity-30 transition-opacity`}
                  aria-label="Next page"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>

              {/* Field Notes — auto-appear at quartile crossings */}
              <FieldNotes
                slug={slug}
                currentPage={currentPage}
                pageCount={totalKnownPages}
                isLightMode={isLightMode}
              />

              {/* Margin Notes — always-on note taking */}
              <MarginNote
                slug={slug}
                currentPage={currentPage}
                pageCount={totalKnownPages}
                isLightMode={isLightMode}
              />
            </>
          )}
        </main>
      </div>

      {/* End-of-edition celebration overlay */}
      {showFinished && (
        <FinishedOverlay
          slug={slug}
          meta={meta}
          recs={recs}
          isLightMode={isLightMode}
          onClose={() => setShowFinished(false)}
          onReturnToShelf={() => navigate("/library")}
        />
      )}

      {/* Editor's Toolkit — universal expert content drawer */}
      <EditorsToolkit
        open={toolkitOpen}
        onClose={() => setToolkitOpen(false)}
        isLightMode={isLightMode}
      />
    </div>
  );
}

// ─── Tiny atoms ─────────────────────────────────────────────────────────
function IconBtn({ onClick, title, children, testId, light }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testId}
      className={`p-2 ${light ? "text-ink/65 hover:text-ink hover:bg-ink/5" : "text-cream/70 hover:text-cream hover:bg-cream/8"} transition-colors`}
    >
      {children}
    </button>
  );
}

function FinishedOverlay({ slug, meta, recs, isLightMode, onClose, onReturnToShelf }) {
  return (
    <div
      className="fixed inset-0 z-[60] bg-ink/95 backdrop-blur-sm overflow-y-auto"
      data-testid="finished-overlay"
    >
      <button
        onClick={onClose}
        className="absolute top-5 right-5 text-cream/70 hover:text-cream p-2"
        aria-label="Close"
        data-testid="finished-close"
      >
        <X className="h-5 w-5" />
      </button>
      <div className="min-h-full flex flex-col items-center justify-start px-6 py-20 text-center text-cream">
        <Sparkles className="h-10 w-10 text-cream/60 mb-6" />
        <div className="overline text-cream/60 mb-3">Edition completed</div>
        <h2 className="font-display uppercase text-5xl md:text-7xl leading-[0.92] max-w-3xl">
          That&apos;s a <span className="serif-italic font-serif normal-case">wrap</span>.
        </h2>
        <p className="font-serif italic text-cream/75 text-lg md:text-xl mt-6 max-w-xl">
          You finished <b className="not-italic">№ {meta.edition_no} — {meta.title}</b>. Your shelf marks it down. Now, what next?
        </p>

        {/* Final Reflection workshop */}
        <FinalReflection slug={slug} isLightMode={false} onContinue={() => {
          const el = document.querySelector('[data-testid="finished-next-reads"]');
          el?.scrollIntoView({ behavior: "smooth" });
        }}/>

        {recs.length > 0 && (
          <div className="mt-14 w-full max-w-5xl" data-testid="finished-next-reads">
            <div className="overline text-cream/55 mb-6 text-left">Hand-picked next reads</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {recs.map((r) => (
                <Link key={r.slug} to={`/studio/${r.slug}`} className="group block text-left">
                  <div className="relative aspect-[3/4] bg-cream/5 overflow-hidden">
                    <img
                      src={`${API}${r.cover_url}`}
                      alt={r.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-ink/95" />
                    <div className="absolute top-3 left-3 font-display text-cream/80 text-base tracking-[0.18em]">№ {r.edition_no}</div>
                    <div className="absolute top-3 right-3 overline text-[9px] bg-cream text-ink px-2 py-1 tracking-[0.22em]">${r.price}</div>
                    <div className="absolute bottom-3 left-3 right-3">
                      <div className="overline text-cream/70 text-[9px] mb-1">{r.tier_label}</div>
                      <div className="font-serif text-cream text-sm leading-snug line-clamp-2">{r.title}</div>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-1 overline text-cream/70 text-[10px]">
                    Add to shelf <ArrowUpRight className="h-3 w-3" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-14 flex flex-wrap gap-3 justify-center">
          <button
            onClick={onReturnToShelf}
            className="inline-flex items-center gap-2 bg-cream text-ink px-7 py-4 overline tracking-[0.3em] hover:bg-cream/90 transition-colors"
            data-testid="finished-back-shelf"
          >
            <BookOpen className="h-4 w-4" /> Back to my shelf
          </button>
          <Link
            to="/studio"
            className="inline-flex items-center gap-2 border border-cream/30 text-cream px-7 py-4 overline tracking-[0.3em] hover:bg-cream hover:text-ink transition-colors"
            data-testid="finished-browse"
          >
            Browse all editions <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}


// ─── Podcast player — used when meta.kind === "audio" ───────────────────
function PodcastPlayer({ slug, episodes, title, palette, isLightMode }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  const activeEp = episodes[activeIdx];
  const audioUrl = activeEp ? `${API}/api/library/audio/${slug}/${encodeURIComponent(activeEp.file)}` : "";

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setCurrentTime(a.currentTime);
    const onMeta = () => setDuration(a.duration);
    const onEnded = () => {
      setPlaying(false);
      // Auto-advance to next episode
      if (activeIdx < episodes.length - 1) {
        setActiveIdx((i) => i + 1);
        setTimeout(() => { a.play().catch(() => {}); setPlaying(true); }, 200);
      }
    };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnded);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnded);
    };
  }, [activeIdx, episodes.length]);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play().catch(() => {}); setPlaying(true); }
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    a.currentTime = pct * duration;
  };

  const fmt = (s) => {
    if (!Number.isFinite(s)) return "0:00";
    const m = Math.floor(s / 60); const sec = Math.floor(s % 60);
    return `${m}:${String(sec).padStart(2, "0")}`;
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;
  const cream = isLightMode ? "#1C1C1E" : "#F3F0E9";

  return (
    <div className="w-full h-full flex flex-col items-center justify-center px-4 md:px-12 py-10" data-testid="podcast-player">
      <audio ref={audioRef} src={audioUrl} preload="metadata" />

      {/* Episode title */}
      <div className="text-center max-w-2xl mb-10">
        <div className={`overline ${palette.subtle} mb-3 text-[10px]`}>Episode {activeEp?.num || "?"} of {episodes.length} · {title}</div>
        <h2 className="font-display uppercase text-3xl md:text-5xl leading-[0.95]">
          {activeEp?.title || "Select an episode"}
        </h2>
      </div>

      {/* Big play button + transport */}
      <div className="w-full max-w-2xl">
        {/* Scrub bar */}
        <div
          className={`relative h-1.5 ${isLightMode ? "bg-ink/15" : "bg-cream/15"} cursor-pointer mb-3`}
          onClick={seek}
          data-testid="podcast-scrub"
        >
          <div
            className="absolute inset-y-0 left-0 transition-[width]"
            style={{ width: `${progress}%`, background: cream }}
          />
        </div>
        <div className={`flex justify-between text-[11px] tabular-nums ${palette.subtle} font-mono mb-8`}>
          <span>{fmt(currentTime)}</span>
          <span>{fmt(duration)}</span>
        </div>

        <div className="flex items-center justify-center gap-8">
          <button
            onClick={() => setActiveIdx((i) => Math.max(0, i - 1))}
            disabled={activeIdx === 0}
            className={`p-3 ${palette.subtle} hover:opacity-100 opacity-80 disabled:opacity-30`}
            aria-label="Previous episode"
            data-testid="podcast-prev"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            onClick={togglePlay}
            className={`w-20 h-20 md:w-24 md:h-24 rounded-full ${isLightMode ? "bg-ink text-cream" : "bg-cream text-ink"} flex items-center justify-center hover:scale-105 transition-transform`}
            aria-label={playing ? "Pause" : "Play"}
            data-testid="podcast-playpause"
          >
            {playing
              ? <svg width="22" height="28" viewBox="0 0 22 28" fill="currentColor"><rect x="2" y="2" width="6" height="24"/><rect x="14" y="2" width="6" height="24"/></svg>
              : <svg width="22" height="26" viewBox="0 0 22 26" fill="currentColor" style={{marginLeft: "4px"}}><path d="M2 2 L20 13 L2 24 Z"/></svg>
            }
          </button>
          <button
            onClick={() => setActiveIdx((i) => Math.min(episodes.length - 1, i + 1))}
            disabled={activeIdx >= episodes.length - 1}
            className={`p-3 ${palette.subtle} hover:opacity-100 opacity-80 disabled:opacity-30`}
            aria-label="Next episode"
            data-testid="podcast-next"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* Episode list */}
      <div className="w-full max-w-2xl mt-14 border-t border-current/15 pt-6">
        <div className={`overline ${palette.subtle} mb-4 text-[10px]`}>All episodes</div>
        <div className="space-y-1">
          {episodes.map((ep, i) => {
            const isActive = i === activeIdx;
            return (
              <button
                key={ep.file}
                onClick={() => { setActiveIdx(i); setPlaying(false); setCurrentTime(0); }}
                data-testid={`podcast-episode-${ep.num}`}
                className={`w-full text-left px-4 py-3 flex items-start gap-4 transition-colors ${isActive ? (isLightMode ? "bg-ink text-cream" : "bg-cream text-ink") : (isLightMode ? "hover:bg-ink/5" : "hover:bg-cream/8")}`}
              >
                <div className="font-display text-sm tracking-[0.15em] w-7 text-right pt-0.5 opacity-70">{String(ep.num).padStart(2, "0")}</div>
                <div className="flex-1 font-serif text-[14px] leading-snug">{ep.title}</div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
